import React from 'react';
import { clientPortalFetch } from './api';
import { printComponent } from '../../utils/printComponent';
import { creditNoteDocument, quotationDocument } from '../../utils/financeDocuments';

// Client-portal downloads. Credit notes and quotations use the same shared
// letterheads as Finance (utils/financeDocuments.js), driven by the firm's
// Billing Settings. Call these straight from a click handler: printComponent
// opens the tab immediately and fills it when the data arrives.

export const downloadCreditNote = (id) => printComponent(async () => {
  const [cn, settings] = await Promise.all([clientPortalFetch(`/credit-notes/${id}`), clientPortalFetch('/doc-settings').catch(() => ({}))]);
  return creditNoteDocument(cn, cn.items, settings);
});

export const downloadQuotation = (quote) => printComponent(async () => {
  const settings = await clientPortalFetch('/doc-settings').catch(() => ({}));
  return quotationDocument(quote, settings);
});

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const day = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

function StatementSheet({ settings, ledger, clientName }) {
  const color = settings.primary_color || '#0f1f45';
  const group = ledger.mode === 'group';
  const th = { padding: '7px 8px', fontSize: 11, textAlign: 'left', background: color, color: '#fff' };
  const td = { padding: '6px 8px', fontSize: 11.5, borderBottom: '1px solid #e5e7eb' };
  const num = { textAlign: 'right', whiteSpace: 'nowrap' };
  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans', Arial, sans-serif", color: '#111827', padding: 8 }}>
      <div style={{ borderBottom: `3px solid ${color}`, paddingBottom: 10, marginBottom: 14 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color }}>{settings.firm_name || ''}</div>
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Statement of Account</div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 14 }}>
        <div><b>{group ? (ledger.group_name || 'Group') : clientName}</b></div>
        <div>As on {day(new Date())}</div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={th}>Date</th><th style={th}>Type</th><th style={th}>Reference</th>
            {group && <th style={th}>Company</th>}
            <th style={{ ...th, ...num }}>Debit</th><th style={{ ...th, ...num }}>Credit</th><th style={{ ...th, ...num }}>Balance</th>
          </tr>
        </thead>
        <tbody>
          {ledger.entries.map((e, i) => (
            <tr key={i}>
              <td style={td}>{day(e.date)}</td><td style={td}>{e.type}</td><td style={td}>{e.reference}</td>
              {group && <td style={td}>{e.company_name}</td>}
              <td style={{ ...td, ...num }}>{e.debit ? money(e.debit) : '—'}</td>
              <td style={{ ...td, ...num }}>{e.credit ? money(e.credit) : '—'}</td>
              <td style={{ ...td, ...num, fontWeight: 700 }}>{money(e.balance)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 14, textAlign: 'right', fontSize: 14, fontWeight: 800, color }}>
        Closing Balance: {money(ledger.closing_balance)}{ledger.closing_balance < 0 ? ' (Advance)' : ''}
      </div>
    </div>
  );
}

export const downloadStatement = (ledger, clientName) => printComponent(async () => {
  const settings = await clientPortalFetch('/doc-settings').catch(() => ({}));
  return { title: `Statement ${clientName || ''}`.trim(), element: <StatementSheet settings={settings} ledger={ledger} clientName={clientName} /> };
});
